import type { SvgIconComponent } from '@mui/icons-material';
import AccountBoxOutlined from '@mui/icons-material/AccountBoxOutlined';
import AccountTreeOutlined from '@mui/icons-material/AccountTreeOutlined';
import AddOutlined from '@mui/icons-material/AddOutlined';
import BarChartOutlined from '@mui/icons-material/BarChartOutlined';
import CastOutlined from '@mui/icons-material/CastOutlined';
import DomainOutlined from '@mui/icons-material/DomainOutlined';
import DonutSmallOutlined from '@mui/icons-material/DonutSmallOutlined';
import GavelOutlined from '@mui/icons-material/GavelOutlined';
import HistoryOutlined from '@mui/icons-material/HistoryOutlined';
import ListOutlined from '@mui/icons-material/ListOutlined';
import ManageAccountsOutlined from '@mui/icons-material/ManageAccountsOutlined';
import PeopleOutlined from '@mui/icons-material/PeopleOutlined';
import ReceiptLongOutlined from '@mui/icons-material/ReceiptLongOutlined';
import SettingsOutlined from '@mui/icons-material/SettingsOutlined';
import SourceOutlined from '@mui/icons-material/SourceOutlined';
import SpeedOutlined from '@mui/icons-material/SpeedOutlined';
import SwapHorizOutlined from '@mui/icons-material/SwapHorizOutlined';
import ThumbsUpDownOutlined from '@mui/icons-material/ThumbsUpDownOutlined';
import TravelExploreOutlined from '@mui/icons-material/TravelExploreOutlined';
import type { Permission } from '@/auth/permissions';

export interface MenuItem {
  /** i18n key in the "menu" namespace */
  key: string;
  path?: string;
  icon?: SvgIconComponent;
  permission?: Permission;
  children?: MenuItem[];
}

export interface MenuSection {
  key: string;
  permission?: Permission;
  items: MenuItem[];
}

export const CASE_VIEWS = ['MY', 'OPEN', 'ESCALATED', 'OVERDUE', 'CLOSED', 'ALL'] as const;
export type CaseView = (typeof CASE_VIEWS)[number];

const caseViewItems = (base: string): MenuItem[] =>
  CASE_VIEWS.map((view) => ({ key: `${view.toLowerCase()}Cases`, path: `${base}?view=${view}` }));

/** Side menu (matches the existing product screens). Visibility per item/section is permission based. */
export const MENU: MenuSection[] = [
  {
    key: 'dashboards',
    items: [
      { key: 'transactionEfficiency', path: '/dashboards/transaction-efficiency', icon: SpeedOutlined },
      { key: 'transactionCaseStatistics', path: '/dashboards/transaction-case-statistics', icon: BarChartOutlined },
      { key: 'customerRiskOverview', path: '/dashboards/customer-risk', icon: DonutSmallOutlined },
    ],
  },
  {
    key: 'customer',
    items: [
      { key: 'customerList', path: '/customers', icon: AccountBoxOutlined },
      { key: 'adHocScreening', path: '/screening/ad-hoc', icon: AddOutlined, permission: 'screening.run' },
    ],
  },
  {
    key: 'cases',
    items: [
      { key: 'customerCases', icon: PeopleOutlined, children: caseViewItems('/cases/customer') },
      { key: 'transactionCases', icon: SwapHorizOutlined, children: caseViewItems('/cases/transaction') },
    ],
  },
  {
    key: 'configurations',
    items: [
      { key: 'settings', path: '/config/settings', icon: SettingsOutlined, permission: 'config.view' },
      { key: 'customerRiskRating', path: '/config/risk-rating', icon: ThumbsUpDownOutlined, permission: 'config.view' },
      { key: 'workflows', path: '/config/workflows', icon: AccountTreeOutlined, permission: 'config.view' },
      { key: 'ruleManager', path: '/config/rules', icon: GavelOutlined, permission: 'config.view' },
      { key: 'listManager', path: '/config/lists', icon: ListOutlined, permission: 'config.view' },
      { key: 'dataFeeds', path: '/config/data-feeds', icon: CastOutlined, permission: 'dataFeeds.view' },
    ],
  },
  {
    key: 'intelligence',
    items: [
      { key: 'watchlistSources', path: '/intelligence/watchlist-sources', icon: SourceOutlined },
      { key: 'watchlistSearch', path: '/intelligence/watchlist-search', icon: TravelExploreOutlined },
      { key: 'transactions', path: '/intelligence/transactions', icon: ReceiptLongOutlined },
      { key: 'auditLog', path: '/intelligence/audit-log', icon: HistoryOutlined, permission: 'audit.view' },
    ],
  },
  {
    key: 'administration',
    items: [
      { key: 'users', path: '/admin/users', icon: ManageAccountsOutlined, permission: 'users.manage' },
      { key: 'tenants', path: '/platform/tenants', icon: DomainOutlined, permission: 'platform.manage' },
    ],
  },
];

/** All i18n keys used by the menu (validated by a unit test against every locale). */
export function menuKeys(sections: MenuSection[] = MENU): string[] {
  const walk = (items: MenuItem[]): string[] => items.flatMap((i) => [i.key, ...walk(i.children ?? [])]);
  return sections.flatMap((s) => [s.key, ...walk(s.items)]);
}
